// ─── geometry ───────────────────────────────────────────────────────────────
// Pure helpers for working with the mm-based apartment/furniture coordinate
// space returned by the backend. Kept dependency-free so they can be unit
// tested in isolation from any rendering code.
//
// Coordinate convention, matching the source dataset and every DTO built
// from it: x increases rightward, y is NEGATIVE-DOWNWARD (i.e. y increases
// *upward* in the source data, so "more negative y" = "further down" on
// screen). Every helper here that touches y assumes this convention; the
// SVG-rendering layer (FloorplanCanvas) is responsible for the one
// necessary sign flip when mapping into screen/SVG space, not these helpers.
//
// Coordinate scope: every room's footprintPolygon and wallSegments, and
// every furniture placement's startPoint/footprintPolygon, are all given
// in ONE SHARED apartment-global coordinate space -- confirmed against
// real upstream data. A room's polygon already encodes exactly where that
// room sits relative to every other room in the same combination; there
// is no per-room re-zeroing to do. Accordingly, the "origin"/"frame"
// arguments threaded through the functions below (originX, originY,
// frameHeight) are expected to describe ONE shared frame for an entire
// combination -- computed once from every room's geometry combined, not
// once per room -- even though nothing in these functions' own math
// requires that; they're generic point-conversion helpers and don't care
// whose bounding box they were given.

/**
 * Bounding box [minX, maxX, minY, maxY] of a polygon point list
 * ([[x, y], ...]) or of multiple lists merged together. Callers building
 * an apartment-wide frame should pass every room's footprintPolygon
 * together in one call (one combined frame for the whole combination),
 * not one call per room -- per-room calls would each produce a different,
 * inconsistent local frame, undoing the fact that the source coordinates
 * already share one apartment-global space.
 */
export function boundingBox(pointLists) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const points of pointLists) {
    for (const [x, y] of points) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  return [minX, maxX, minY, maxY];
}

/**
 * Uniform scale factor that fits a sourceWidth x sourceHeight extent into
 * a boxWidth x boxHeight slot without distortion ("fit, don't stretch").
 * Used once per combination, against the whole apartment's bounding-box
 * extent and the SVG viewBox's extent -- not once per room -- since every
 * room already shares one coordinate space and only needs one shared
 * scale to render correctly relative to each other.
 */
export function fitScale(sourceWidth, sourceHeight, boxWidth, boxHeight) {
  return Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
}

/**
 * Converts a point from the shared "whole apartment" coordinate system
 * (x = right, y = up) into local, top-left-origin coordinates, with y
 * flipped to increase downward (matching screen/SVG convention).
 *
 * Which frame to pass depends on what you're drawing:
 *
 * - Drawing MULTIPLE rooms together (a combination view, e.g.
 *   FloorplanCanvas) -> originX/originY/frameHeight must come from ONE
 *   shared frame covering all of them, typically
 *   boundingBox(combination.rooms.map(r => r.footprintPolygon)).
 *   Using a single room's own bounding box here would re-zero that room
 *   on its own, losing its real position relative to the other rooms.
 *
 * - Drawing ONE room by itself, with no other rooms in the same view
 *   (e.g. QcLayoutTile, LayoutThumb) -> that room's own bounding box
 *   IS the correct frame, since there's nothing else to stay relative to.
 */
export function toLocalPoint([x, y], originX, originY, frameHeight) {
  const localX = x - originX;
  const localY = frameHeight - (y - originY);
  return [localX, localY];
}

/**
 * Maps an entire polygon (a room footprint, a wall segment's two
 * endpoints, etc.) into the shared local, top-left-origin, y-down
 * coordinate space in one pass, using the same combination-wide frame
 * described in toLocalPoint above.
 */
export function polygonToLocal(points, originX, originY, frameHeight) {
  return points.map((pt) => toLocalPoint(pt, originX, originY, frameHeight));
}

/**
 * Builds the SVG path `d` attribute for a closed polygon already in
 * local (or any consistent) coordinate space, optionally pre-scaled.
 * Used for both the room footprint outline and, in principle, any other
 * closed shape -- kept generic rather than room-specific.
 */
export function polygonToPathD(localPoints, scale = 1) {
  if (localPoints.length === 0) return '';
  const [first, ...rest] = localPoints;
  let d = `M ${first[0] * scale} ${first[1] * scale}`;
  for (const [x, y] of rest) {
    d += ` L ${x * scale} ${y * scale}`;
  }
  return d + ' Z';
}

/**
 * Figures out how to draw one piece of furniture on the screen:
 * where to place its image, and how much to rotate it, so it ends
 * up in the exact right spot.
 *
 * Rule we're following: the furniture's un-rotated rectangle starts
 * at startPoint, with startPoint as its top-left corner. Before any
 * rotation, it extends to the right and downward. Rotation is then
 * applied around that same startPoint.
 *
 * startPoint uses the same coordinate system as the room shapes and
 * walls. That means originX, originY, and frameHeight here must be
 * the same values used for the whole combination -- not just one
 * room -- otherwise the furniture won't line up with the room it
 * belongs to.
 *
 * Returns a position (measured from the top-left, with y going
 * downward) and a rotation instruction for SVG, both already
 * resized using `scale`.
 *
 * piece: { nativeWidthMm, nativeHeightMm } (a FurniturePieceDto)
 * placement: { startPointXMm, startPointYMm, rotationRadians } (a FurniturePlacementDto)
 */
export function placementToLocalRect(piece, placement, originX, originY, frameHeight, scale) {
  const { nativeWidthMm: w, nativeHeightMm: h } = piece;
  const { startPointXMm, startPointYMm, rotationRadians } = placement;

  // Unrotated rect's top-left corner, in local (top-left-origin, y-down) space.
  const [localX, localY] = toLocalPoint([startPointXMm, startPointYMm], originX, originY, frameHeight);

  // The source rotation is defined in the source's own y-up coordinate
  // space; flipping to a y-down local space negates the effective
  // rotation direction as seen on screen, so the sign is inverted here
  // to keep the visual rotation correct after the axis flip.
  const rotationDegrees = -(rotationRadians * 180) / Math.PI;

  return {
    x: localX * scale,
    y: localY * scale,
    width: w * scale,
    height: h * scale,
    // SVG rotate(angle, cx, cy) rotates about (cx, cy) -- the same
    // start-point anchor used to position the rect, scaled to match.
    transform: `rotate(${rotationDegrees} ${localX * scale} ${localY * scale})`,
  };
}

// ─── polygon helpers ────────────────────────────────────────────────────────
// General-purpose polygon math. pointInPolygon is used by
// insetRectilinearPolygon below (to decide which side of an edge is
// "inward"); polygonCentroid is used directly by FloorplanCanvas, for the
// centered label on a room with no room-uses at all. Both operate in the
// same apartment-global source space as footprintPolygon/wallSegments
// (before toLocalPoint), since point-in-polygon and centroid math don't
// care about axis orientation as long as the point and polygon use the
// same convention.

/**
 * Standard ray-casting point-in-polygon test. Draws a ray from the point
 * out along +x and counts how many polygon edges it crosses; odd means
 * inside, even means outside. O(number of edges) -- a room typically has
 * well under a dozen vertices, so this is cheap even run several times
 * per room per render.
 */
export function pointInPolygon([px, py], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const crossesRay = (yi > py) !== (yj > py) &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (crossesRay) inside = !inside;
  }
  return inside;
}

/**
 * True area-weighted centroid of a polygon (shoelace formula), NOT a
 * naive average of vertices -- a vertex average can land outside a
 * concave (e.g. L-shaped) polygon, which defeats the point of using a
 * centroid as a fallback anchor in the first place.
 */
export function polygonCentroid(polygon) {
  let signedArea = 0, cx = 0, cy = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [x0, y0] = polygon[i];
    const [x1, y1] = polygon[(i + 1) % polygon.length];
    const cross = x0 * y1 - x1 * y0;
    signedArea += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  signedArea /= 2;
  if (Math.abs(signedArea) < 1e-9) {
    // Degenerate (near-zero-area) polygon -- fall back to a plain vertex
    // average rather than dividing by ~zero.
    const n = polygon.length || 1;
    const [sx, sy] = polygon.reduce(([ax, ay], [x, y]) => [ax + x, ay + y], [0, 0]);
    return [sx / n, sy / n];
  }
  return [cx / (6 * signedArea), cy / (6 * signedArea)];
}

// ─── polygon offset helper ──────────────────────────────────────────────────

/**
 * Inward-offsets a RECTILINEAR polygon (every edge purely horizontal or
 * vertical -- true for every room shape this app draws, rectangle or
 * L-shaped) by a fixed real-world distance, with a mitre join at each
 * corner. This mirrors the pipeline's own
 * `polygon.buffer(-62, join_style="mitre")` (see structure_dataEntry5.py's
 * placementPerimeter) closely enough to reuse for on-canvas drawing --
 * without needing that value to actually be sent by the backend.
 *
 * Only correct for axis-aligned edges: for a horizontal edge, offsetting
 * "inward" just means shifting its whole line up or down by `distanceMm`;
 * for a vertical edge, shifting left or right. Two consecutive offset
 * edges (one horizontal, one vertical) then trivially intersect at
 * (the vertical line's x, the horizontal line's y) -- no general line-
 * intersection math needed, which is what makes this safe to do on the
 * client for shapes this simple.
 *
 * Which direction is "inward" is decided per edge by nudging its
 * midpoint a small test distance to each side and keeping whichever side
 * lands inside the polygon (via pointInPolygon) -- this works correctly
 * for concave (L-shaped) rooms too, since it's decided locally per edge
 * rather than assumed from the whole shape's centroid.
 *
 * `points` may be closed (first point repeated as last, as the backend's
 * closed_polygon_json always sends it) or open -- both are accepted.
 * Returns a closed point list in the same convention as the input.
 *
 * If any edge isn't axis-aligned (shouldn't happen for this app's data),
 * that edge -- and any corner touching it -- is left at its original
 * position rather than guessed at, so a bad input produces a visibly odd
 * dashed line instead of a silently wrong one.
 */
export function insetRectilinearPolygon(points, distanceMm) {
  const first = points[0];
  const last = points[points.length - 1];
  const isClosed = points.length > 1 && first[0] === last[0] && first[1] === last[1];
  const pts = isClosed ? points.slice(0, -1) : points;
  const n = pts.length;

  if (n < 3) return points;

  // One offset "line" per edge: a horizontal edge offsets to another
  // horizontal line (a new y value), a vertical edge offsets to another
  // vertical line (a new x value).
  const edgeLines = pts.map((point, i) => {
    const [x0, y0] = point;
    const [x1, y1] = pts[(i + 1) % n];
    const midX = (x0 + x1) / 2;
    const midY = (y0 + y1) / 2;

    if (y0 === y1) {
      const candidateUp = y0 + distanceMm;
      const candidateDown = y0 - distanceMm;
      const value = pointInPolygon([midX, candidateUp], pts) ? candidateUp : candidateDown;
      return { horizontal: true, value };
    }
    if (x0 === x1) {
      const candidateRight = x0 + distanceMm;
      const candidateLeft = x0 - distanceMm;
      const value = pointInPolygon([candidateRight, midY], pts) ? candidateRight : candidateLeft;
      return { horizontal: false, value };
    }
    // Not axis-aligned -- flag so the corners touching this edge fall
    // back to their original position instead of being guessed at.
    return { horizontal: null };
  });

  const insetPoints = pts.map((point, i) => {
    const prev = edgeLines[(i - 1 + n) % n];
    const curr = edgeLines[i];

    if (prev.horizontal === null || curr.horizontal === null || prev.horizontal === curr.horizontal) {
      // Either a non-rectilinear edge touches this corner, or two
      // consecutive edges run the same direction (shouldn't happen in a
      // clean rectilinear polygon) -- keep the original point rather
      // than guess.
      return point;
    }

    return prev.horizontal
      ? [curr.value, prev.value] // prev is horizontal (sets y), curr is vertical (sets x)
      : [prev.value, curr.value]; // prev is vertical (sets x), curr is horizontal (sets y)
  });

  return [...insetPoints, insetPoints[0]];
}